import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Plus, User, Users } from "lucide-react";
import { useNavigate } from "react-router";
import { useMe } from "@/hooks/useMe";
import { api, setWorkspaceId } from "@/lib/api";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "./ui/menu";
import { t } from "@shared/i18n";

export type WorkspaceSummary = { id: string; name: string; isPersonal: boolean; plan: string; role: string };

export const ROLE_LABELS: Record<string, string> = { owner: t("Propriétaire"), admin: t("Administrateur"), manager: t("Responsable"), member: t("Membre"), guest: t("Invité (lecture seule)") };

export function useWorkspaces() {
  return useQuery({ queryKey: ["team", "workspaces"], queryFn: async () => (await api<{ data: { workspaces: WorkspaceSummary[] } }>("/api/team")).data.workspaces, staleTime: 60_000 });
}

/** Bascule entre l'espace personnel (privé) et les espaces d'équipe. */
export function WorkspaceSwitcher() {
  const { data: me } = useMe();
  const list = useWorkspaces();
  const qc = useQueryClient();
  const navigate = useNavigate();
  if (!me) return null;
  const current = me.workspace;
  const switchTo = async (w: WorkspaceSummary) => {
    if (w.id === current.id) return;
    setWorkspaceId(w.isPersonal ? null : w.id);
    navigate("/app");
    await qc.resetQueries();
  };
  return (
    <Menu>
      <MenuTrigger asChild>
        <button className="mb-3 flex h-10 w-full items-center gap-2 rounded-lg border border-border bg-elevated px-3 text-left text-[13px] hover:border-border-strong" aria-label={t("Changer d'espace de travail")}>
          {current.isPersonal ? <User className="size-4 text-muted" /> : <Users className="size-4 text-primary" />}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{current.isPersonal ? t("Espace personnel") : current.name}</span>
            {!current.isPersonal && <span className="block truncate text-[11px] text-muted">{ROLE_LABELS[current.role]}</span>}
          </span>
          <ChevronsUpDown className="size-4 text-subtle" />
        </button>
      </MenuTrigger>
      <MenuContent align="start" className="w-60">
        <MenuLabel>{t("Espaces de travail")}</MenuLabel>
        {list.data?.map((w) => (
          <MenuItem key={w.id} icon={w.id === current.id ? <Check /> : w.isPersonal ? <User /> : <Users />} onSelect={() => switchTo(w)}>
            {w.isPersonal ? t("Espace personnel") : w.name}
          </MenuItem>
        ))}
        <MenuSeparator />
        <MenuItem icon={<Plus />} onSelect={() => navigate("/app/team?new=1")}>
          {t("Créer un espace d'équipe")}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
